import { SCKpis } from "../generic/generic-sc-domain";
import { IClientAccount } from "./types";
import { AgGridUserPreference } from "../sg-dashboard/userPreference/userPreference.typings";
import { isArray, map, replace } from "lodash";
import { getFieldInformations } from "../sg-dashboard/userPreference/userpreference.map";
import { getAllScHeaders } from "../../cpr-salescredit-client-list/utils/Headers";
import { isNotNullOrEmpty } from "../generic/generic-helper";

export const Functions = {
  calculateNbiVar,
  calculateNbiVarWithoutPercent,
  calculateProgressMade,
  calculateProgress,
  calculateVariation,
  formatMoney,
  formatMoneyByMillion,
  formatMoneyNoDecimal,
  formatMoneyNoDecimalWithPercentage,
  formatMoneyWithDecimal,
  formatMoneyWithoutDecimal,
  formatNoDecimal,
  fpvBlHlink,
  fpvSblHlink,
  fpvSsblHlink,
  hlinkFunction,
  hlinkFunctionForGroup,
  hlinkFunctionForLE,
  hlinkFunctionForLERct,
  hlinkFunctionForSubGroup,
  reOrderArray,
  reOrderArrayObj,
  suppressCount,
};

//#region Formatter function

export function suppressCount(params: any) {
  // to remove the row count against nodes
  params.node.allChildrenCount = -1;
  // to fix the unnecessary indent in the attributes
  params.node.uiLevel = 0;
  return params.value;
}

export function formatMoney(value: number) {
  if (isNaN(value)) {
    return 0;
  }
  return Number(value / 1000);
}

export function formatMoneyWithoutDecimal(value: any) {
  if (isNaN(value)) {
    return 0;
  }
  const converted = value;
  return converted != null ? Number((converted / 1000).toFixed()) : 0;
}

export function formatMoneyNoDecimal(value: any) {
  if (isNaN(value.value)) {
    return 0;
  }
  const converted = value.value;
  return converted != null ? Number(converted.toFixed()) : 0;
}

export function formatMoneyWithDecimal(value: any) {
  if (!value || isNaN(value.value)) {
    return 0;
  }
  const converted = value.value;
  return Number(converted).toLocaleString("en-US", {
    maximumFractionDigits: 3,
    minimumFractionDigits: 3,
  });
}

export function formatMoneyByMillion(val: number, shouldNotFix = false) {
  if (!val || isNaN(val)) {
    return 0;
  }
  const converted = val / 1000000;
  return Number(shouldNotFix ? converted : converted.toFixed());
}

export function formatNoDecimal(value: number, shouldNotFix = false) {
  if (
    !value ||
    isNaN(value) ||
    value.toString().toUpperCase() === "INFINITY" ||
    value.toString().toUpperCase() === "-INFINITY"
  ) {
    return 0;
  }
  const converted = value * 100;
  return Number(shouldNotFix ? converted : converted.toFixed());
}

export function calculateNbiVarWithoutPercent(params: any) {
  return calculateNbiVar(params, true);
}
const VariationFactory: {
  [board: string]: { num1: any; num2: any };
} = {
  nbi_forcast_y1_var_yoy: { num1: "nbi_forcast_y1", num2: "nbi_fy_1" },
  nbi_forecast_rwa_y1_vs_fy_1: {
    num1: "nbi_forcast_rwa_y1",
    num2: "nbi_rwa_fy_1",
  },
  nbi_fy_var_yoy: { num1: "nbi_ytd", num2: "nbi_fy_1" },
  nbi_rgcp_vs_fy_1: { num1: "nbi_rgcp", num2: "nbi_rgcp_1" },
  nbi_rwa_vs_fy_1: { num1: "nbi_rwa_avg", num2: "nbi_rwa_fy_1" },
  nbi_ytd_var_yoy: { num1: "nbi_ytd", num2: "nbi_ytd_1" },
  nbi_ytd_vs_nbi_gbis_bud_obj: { num1: "nbi_ytd", num2: "nbi_gbis_bud_obj" },
  nbi_ytd_vs_nbi_gbis_bud_obj_ib: {
    num1: "nbi_ytd_ib",
    num2: "nbi_gbis_bud_obj_ib",
  },
  sc_mtd_yoy: {
    num1: SCKpis.SC_MTD,
    num2: SCKpis.SC_MTD_1,
  },
  sc_ytd_var_yoy: {
    num1: "sc_ytd",
    num2: "sc_ytd_1",
  },
};
export function calculateNbiVar(params: any, renderWithoutPercent = false) {
  let result = 0;
  const field = params.colDef.field;
  const VarianceParameters = VariationFactory[field];
  const param1 = VarianceParameters.num1;
  const param2 = VarianceParameters.num2;
  const num1 = params.node.aggData[param1];
  const num2 = params.node.aggData[param2];
  result = calculateVariation(num1, num2);
  return getVarianceStyle(result, renderWithoutPercent);
}

function getVarianceStyle(result: number, renderWithoutPercent = false) {
  if (renderWithoutPercent) {
    return result;
  }
  if (result > 0) {
    return `<div style = color:#088A08; padding-right:25px; text-align:right> +${result}% </div>`;
  } else {
    return `<div style = color:#FF0000; padding-right:25px; text-align:right> ${result}% </div>`;
  }
}

export function calculateVariation(
  num: number,
  num1: number,
  shouldNotFix = false
) {
  num = num === undefined || isNaN(num) || !isFinite(num) ? 0 : num;
  num1 = num1 === undefined || isNaN(num1) || !isFinite(num) ? 0 : num1;
  const variation = num1 === 0 ? 0 : ((num - num1) / Math.abs(num1)) * 100;
  return checkForFixPointDecimals(variation, shouldNotFix);
}

export function calculateProgressMade(params: any) {
  let result = 0;
  const progressParams = VariationFactory[params.colDef.field];
  const num1 = params.node.aggData[progressParams.num1];
  const num2 = params.node.aggData[progressParams.num2];
  result = calculateProgress(num1, num2);
  return getVarianceStyle(result, false);
}

export function calculateProgress(num: number, num1: number) {
  num = num === undefined || isNaN(num) || !isFinite(num) ? 0 : num;
  num1 = num1 === undefined || isNaN(num1) || !isFinite(num) ? 0 : num1;
  const progress = num1 === 0 || num <= 0 ? 0 : (num / num1) * 100;
  return checkForFixPointDecimals(progress, false);
}

function checkForFixPointDecimals(variation: number, shouldNotFix = false) {
  return Number(
    isNaN(variation) ||
      variation.toString().toUpperCase() === "INFINITY" ||
      variation.toString().toUpperCase() === "-INFINITY"
      ? 0
      : variation > 0
      ? shouldNotFix
        ? variation
        : variation.toFixed(0)
      : shouldNotFix
      ? variation
      : variation.toFixed()
  );
}
export function fpvBlHlink(params: any, data: any) {
  return renderSupressedHlink(
    params,
    params.node.allLeafChildren[0].data.fpvBlHlink
  );
}
export function fpvSblHlink(params: any, data: any) {
  return renderSupressedHlink(
    params,
    params.node.allLeafChildren[0].data.fpvSblHlink
  );
}
export function fpvSsblHlink(params: any, data: any) {
  return renderSupressedHlink(
    params,
    params.node.allLeafChildren[0].data.fpvSsblHlink
  );
}
export function renderSupressedHlink(params: any, hlink: any) {
  // to remove the row count against nodes
  params.node.allChildrenCount = -1;
  // to fix the unnecessary indent in the attributes
  params.node.uiLevel = 0;
  return renderLink(hlink, params.value);
}

export function hlinkFunction(params: any, data: any) {
  return renderLink(params.node.allLeafChildren[0].data.hlink, params.value);
}

export function hlinkFunctionForGroup(params: any, data: any) {
  return renderLink(
    params.node.allLeafChildren[0].data.clientGroupNameHlink,
    params.value
  );
}
export function hlinkFunctionForSubGroup(params: any, data: any) {
  return renderLink(
    params.node.allLeafChildren[0].data.clientSubGroupHlink,
    params.value
  );
}
export function hlinkFunctionForLE(params: any, data: any) {
  return renderLink(
    params.node.allLeafChildren[0].data.clientLegalEntityHlink,
    params.value
  );
}
export function hlinkFunctionForLERct(params: any, data: any) {
  return renderLink(
    params.node.allLeafChildren[0].data.clientRctForLegalEntityHlink,
    params.value
  );
}
function renderLink(href: string, value: string) {
  return `<a href=${href} target="_blank" style="text-decoration: underline; color: black;">${value} </a>`;
}
export function reOrderArrayObj(
  sourceArray: any[],
  orderedArray: string[],
  key: string
) {
  sourceArray.sort((a: any, b: any) => {
    const A = a[key];
    const B = b[key];

    if (orderedArray.indexOf(A) > orderedArray.indexOf(B)) {
      return 1;
    } else {
      return -1;
    }
  });
  return sourceArray;
}

export function reOrderArray(sourceArray: string[], orderedArray: string[]) {
  const otherItems = sourceArray.filter(
    (x: any) => orderedArray.indexOf(x) === -1
  );
  let commonItems = sourceArray.filter(
    (x: any) => orderedArray.indexOf(x) > -1
  );
  commonItems.sort((a: any, b: any) => {
    if (orderedArray.indexOf(a) > orderedArray.indexOf(b)) {
      return 1;
    } else {
      return -1;
    }
  });
  commonItems = commonItems.concat(otherItems);
  return commonItems;
}

export function getColumnSelected(
  filters:
    | { [attribute: string]: string[] | string | IClientAccount[] }
    | undefined
    | any,
  userPreferences: AgGridUserPreference,
  Referentiel: any[]
): string[] {
  let columnsSelected: any;
  if (filters !== undefined && filters.columns && filters.columns.length > 0) {
    columnsSelected = filters.columns;
  }

  if (
    userPreferences &&
    userPreferences !== undefined &&
    userPreferences.columns !== undefined &&
    isArray(userPreferences.columns)
  ) {
    const userPreferenceColIds = map(userPreferences.columns, clmn => {
      return clmn.colId ? clmn.colId.replace("ag-Grid-AutoColumn-", "") : "";
    }).filter(elem => elem !== "");

    const userPreferenceColumnsheader = getFieldInformations(
      Referentiel,
      userPreferenceColIds,
      "headerName"
    );
    columnsSelected =
      columnsSelected !== undefined && columnsSelected.length > 0
        ? reOrderArray(columnsSelected, userPreferenceColumnsheader)
        : userPreferenceColumnsheader;
  }
  return columnsSelected;
}

export function formatMoneyNoDecimalWithPercentage(value: any) {
  return `${formatMoneyNoDecimal(value)}%`;
}

export const maxSelectedClients = 100;
